/**
 * This is an implementation of an interface that can be used to extend objects with loadable functionality. This
 * came about because there were several Types that were implementing very similar functionality, but it didn't
 * make sense to derive each off of a base Type.
 */
import Backbone from 'backbone';

import Localizer from 'localizer';
import ModelSynchronizer from 'utils/ModelSynchronizer';
import OptionalDependency from 'utils/extensions/OptionalDependency';

import { restrictedPropertyFiles } from 'utils/WindowPreferencesMappings';
import ViewerSocialProfile from 'models/user/ViewerSocialProfile';

var Events = {
    ON_DEPENDENCIES_LOADED: 'ON_DEPENDENCIES_LOADED',
    ON_DEPENDENCIES_LOAD_FAILED: 'ON_DEPENDENCIES_LOAD_FAILED'
};

var Loadable = {
    //--------------------------------------------------------------------------------------------------------------
    // These are the functions and properties that can or need to be implemented by the Type extending this interface.
    //--------------------------------------------------------------------------------------------------------------

    /**
     * Provide this implementation to add dependencies to the ModelSynchronizer. Allows the implementation to
     * provide additional dependencies.
     * @param modelSynchronizer the synchronizer to add the dependencies to.
     * @param tripCount Used to indicate which round trip this call corresponds to. See onDependenciesLoaded.
     */
    provideDependencies: function(modelSynchronizer, tripCount) {},

    /**
     * Same as provideDependencies except that it is intended for the base implementation. This is necessary due to
     * the lack of inheritance in Javascript.
     *
     * @param modelSynchronizer the synchronizer to add the dependencies to.
     */
    provideCommonDependencies: function(modelSynchronizer) {},

    /**
     * May be overridden to kick off some process required by the ModelSynchronizer. For example, if an object is
     * being synchronized on an event, this method can be overridden to start the process that fires that event.
     * @param tripCount Used to indicate which round trip this call corresponds to. See onDependenciesLoaded.
     */
    loadDependencies: function(tripCount) {},

    /**
     * May be overridden to perform something once this Widgets dependencies have been loaded. If you return false
     * from this function, an additional fetch will be executed. You can provide these additional
     * dependencies in provideDependencies() and loadDependencies.
     *
     * @param tripCount The index that represents this round trip of dependency fetches
     */
    onDependenciesLoaded: function(tripCount) {},

    /**
     * May be overridden to provide error handling if a required dependency cannot be loaded.
     * @param response
     */
    onDependenciesLoadFailed: function(response) {},

    /**
     * This is a list of property files that need to be loaded by the Object.
     */
    propertyFiles: [],

    /**
     * List is a list of optional RequireJS dependencies that can be requested by the extending object.
     */
    optionalDependencies: [],

    /**
     * Keeps track of round trips that have been completed as dependencies are loaded
     */
    tripCount: 0,

    getOptionalDependency: function(keyName) {
        var optionalDependency = _.find(this.optionalDependencyInstances, function(optionalDependency) {
            return optionalDependency.options.key === keyName;
        });

        // If the module couldn't be loaded, return null
        if (optionalDependency.options.module) {
            return optionalDependency.options;
        } else {
            return null;
        }
    },

    getOptionalDependencies: function(keyName) {
        return _.map(this.optionalDependencyInstances, function(optionalDependency) {
            return optionalDependency.options;
        });
    },

    //--------------------------------------------------------------------------------------------------------------
    // Everything below here is the implementation of this "Abstract Class" (for lack of a better term).
    //--------------------------------------------------------------------------------------------------------------

    /**
     * Loads the dependencies for this Widget. Signals
     */
    load: function() {
        var modelSynchronizer = new ModelSynchronizer();
        const userRoles = ViewerSocialProfile.get('userRoles');
        const windowPreferences = window.PREFERENCES || {};

        _.each(
            this.propertyFiles,
            function(propertyFile) {
                const restrictedRoleKey = restrictedPropertyFiles[propertyFile];
                if (restrictedRoleKey) {
                    const userRolesToRestrictTo = windowPreferences[restrictedRoleKey]
                        ? windowPreferences[restrictedRoleKey].split(',')
                        : null;
                    if (
                        userRolesToRestrictTo &&
                        userRoles &&
                        userRolesToRestrictTo.some((role) => userRoles.indexOf(role.trim()) >= 0)
                    ) {
                        modelSynchronizer.addEvent({
                            model: this,
                            event: propertyFile,
                            required: true
                        });
                    }
                } else {
                    modelSynchronizer.addEvent({
                        model: this,
                        event: propertyFile,
                        required: true
                    });
                }
            },
            this
        );

        // This is our list of dependencies that can be declared and optionally loaded.
        if (this.optionalDependencies && this.optionalDependencies.length) {
            this.optionalDependencyInstances = [];

            _.each(
                this.optionalDependencies,
                function(optionalDependencyJson) {
                    var optionalDependency = new OptionalDependency(this, optionalDependencyJson);
                    this.optionalDependencyInstances.push(optionalDependency);

                    // Allows optional dependencies to load based on a truth test
                    if (optionalDependency.shouldLoad()) {
                        modelSynchronizer.addEvent({
                            model: optionalDependency,
                            event: OptionalDependency.Events.Fetched,
                            required: true
                        });
                    }
                },
                this
            );
        }

        // We need to reset the tripCount here because on subsequent "load" calls, if the loadable view is not removed and re-created, we would get tripCount 0 in "provideDependencies", but 1 in "onDependenciesLoaded"
        this.tripCount = 0;

        // Allows implementation to provide dependencies that must be loaded or happen prior to render
        if (typeof this.provideDependencies !== 'undefined') {
            this.provideDependencies.call(this, modelSynchronizer, this.tripCount);
        }

        if (typeof this.provideCommonDependencies !== 'undefined') {
            this.provideCommonDependencies.call(this, modelSynchronizer, this.tripCount);
        }

        modelSynchronizer.bind(ModelSynchronizer.Events.SYNCHRONIZED, this.onLoadableDependenciesLoaded, this);
        modelSynchronizer.bind(ModelSynchronizer.Events.SYNCHRONIZE_FAILED, this.onLoadableDependenciesFailed, this);

        this.loadStartTime = new Date().getTime();

        // The implementation may need to kick off a process in order to satisfy one of the dependencies. The
        // loadDependencies function provides a means to do this.
        if (typeof this.loadDependencies !== 'undefined') {
            this.loadDependencies.call(this, this.tripCount);
        }

        modelSynchronizer.fetchModels();

        _.each(
            this.propertyFiles,
            function(propertyFile) {
                const restrictedRoleKey = restrictedPropertyFiles[propertyFile];
                if (restrictedRoleKey) {
                    const userRolesToRestrictTo = windowPreferences[restrictedRoleKey]
                        ? windowPreferences[restrictedRoleKey].split(',')
                        : null;
                    if (
                        userRolesToRestrictTo &&
                        userRoles &&
                        userRolesToRestrictTo.some((role) => userRoles.indexOf(role.trim()) >= 0)
                    ) {
                        Localizer.loadPropertyFile(propertyFile, this.onPropertyFileLoaded, this);
                    }
                } else {
                    Localizer.loadPropertyFile(propertyFile, this.onPropertyFileLoaded, this);
                }
            },
            this
        );

        if (this.optionalDependencyInstances && this.optionalDependencyInstances.length) {
            _.each(
                this.optionalDependencyInstances,
                function(optionalDependency) {
                    optionalDependency.load();
                },
                this
            );
        }
    },

    /**
     * Called when content is ready. This is a signal that it is ready to be rendered.
     */
    onLoadableDependenciesLoaded: function() {
        var result = this.onDependenciesLoaded(this.tripCount);

        if (result != false) {
            this.elapsedLoadTime = new Date().getTime() - this.loadStartTime;
            this.loaded = true;

            this.trigger(Events.ON_DEPENDENCIES_LOADED, this);
        } else {
            // The first round of dependencies have loaded, however, there are additional dependencies
            // to be fetched based on the result of the first.
            this.scheduleAdditionalFetch();
        }
    },

    /**
     * Called when the dependencies for this object could not be loaded.
     * @param response
     */
    onLoadableDependenciesFailed: function(response) {
        this.onDependenciesLoadFailed(response);
        this.trigger(Events.ON_DEPENDENCIES_LOAD_FAILED, response);
    },

    /**
     * Schedules an additional fetch of dependencies. Calls provideDependencies again to allow the implementor
     * to provide additional dependencies to be loaded.
     */
    scheduleAdditionalFetch: function() {
        this.tripCount++;

        var modelSynchronizer = new ModelSynchronizer();

        // Allows implementation to provide dependencies that must be loaded or happen prior to render
        if (typeof this.loadDependencies != 'undefined') {
            this.provideDependencies.call(this, modelSynchronizer, this.tripCount);
        }

        modelSynchronizer.bind(ModelSynchronizer.Events.SYNCHRONIZED, this.onLoadableDependenciesLoaded, this);
        modelSynchronizer.bind(ModelSynchronizer.Events.SYNCHRONIZE_FAILED, this.onLoadableDependenciesFailed, this);

        // The implementation may need to kick off a process in order to satisfy one of the dependencies. The
        // loadDependencies function provides a means to do this.
        if (typeof this.loadDependencies != 'undefined') {
            this.loadDependencies.call(this, this.tripCount);
        }

        modelSynchronizer.fetchModels();
    },

    /**
     * Called when a property file is loaded. Trigger an event on this View that the syncrhonizer will listen to to
     * know when the property file has been loaded.
     *
     * @param path the path to the property file that was loaded.
     */
    onPropertyFileLoaded: function(path) {
        this.trigger(path);
    },

    /**
     * Returns true if this object has been loaded.
     * @return {boolean}
     */
    isLoaded: function() {
        return !(typeof this.elapsedLoadTime === 'undefined' || this.elapsedLoadTime === null);
    }
};

Loadable.Loadable = {};
Loadable.Loadable.Events = Events;

export default Loadable;
